// ---------------------------------------------------------------------------
// Shared formatting utilities for reimagined pages
// ---------------------------------------------------------------------------

/**
 * Format a number as US currency string (e.g., "$42,000").
 *
 * Uses `Intl.NumberFormat` with zero fraction digits by default.
 * Pass `fractionDigits` to include cents.
 */
export function formatCurrency(amount: number | undefined | null, fractionDigits = 0): string {
  if (amount == null || Number.isNaN(amount)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
}

/**
 * Format a date as "Jan 2025" (short month + year).
 */
export function formatMonthYear(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(d);
}

/**
 * Format a date as "Jan 15, 2025" (short month + day + year).
 */
export function formatShortDate(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

/**
 * Format a date as "1/15/2025" (numeric month/day/year).
 */
export function formatNumericDate(date: Date | string | undefined | null): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: '2-digit',
    year: 'numeric',
  }).format(d);
}

/**
 * Format a benefit cycle string ("YYYY-MM") as "Jan 2025" (short month + year).
 *
 * Falls back to standard date parsing for non-cycle strings.
 */
export function formatCycleDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';
  const parts = dateStr.split('-');
  if (parts.length >= 2) {
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1);
    return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
  }
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(
    new Date(dateStr)
  );
}
